import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { tc } from '../../lib/trackingContext';

export type LookSource = 'lookview_page' | 'trunkshow_looks_module';

export type LookEvent =
  | 'Look Carousel Clicked'
  | 'Shop By Look Clicked'
  | 'Next Look Clicked'
  | 'Shop By Look Module Closed';

type LookTrackingValue = {
  lookId?: string;
  lookSource?: LookSource;
};

const LookTrackingContext = React.createContext<LookTrackingValue>({});

interface Props {
  lookId?: string;
  lookSource: LookSource;
  tracksViews?: boolean;
  children?: React.ReactNode;
}

export const LookTracking: React.FC<Props> = ({
  lookId,
  lookSource,
  tracksViews = true,
  children
}) => {
  useEffect(() => {
    if (!lookId || !tracksViews) return;

    tc.track('Look Module Viewed', {
      addToTrackingEvent: { look_id: lookId, look_source: lookSource }
    });
  }, [lookId, lookSource, tracksViews]);

  const value = useMemo(() => ({ lookId, lookSource }), [lookId, lookSource]);

  return <LookTrackingContext.Provider value={value}>{children}</LookTrackingContext.Provider>;
};

export const useLookTracking = () => useContext(LookTrackingContext);

export const useTrackLookEvent = () => {
  const { lookId, lookSource } = useLookTracking();

  return useCallback(
    (event: LookEvent, properties: Record<string, string | null> = {}) =>
      tc.track(event, {
        addToTrackingEvent: {
          look_id: lookId ?? null,
          look_source: lookSource ?? null,
          ...properties
        }
      }),
    [lookId, lookSource]
  );
};
